import { css } from '@emotion/react';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { FormHelperText, FormLabel } from '@mui/material';
import { mergeRegister } from '@lexical/utils';
import { BLUR_COMMAND, COMMAND_PRIORITY_LOW, EditorState, FOCUS_COMMAND, LexicalEditor } from 'lexical';
import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldRefTarget } from '../../util/formula-vocabulary';
import { AutoTokenizePlugin } from './AutoTokenizePlugin';
import { CommaCompletionPlugin } from './CommaCompletionPlugin';
import { CHIP_VARIANT_CSS, chipShapeCss } from './chipStyles';
import { ErrorUnderlinePlugin, TextRange } from './ErrorUnderlinePlugin';
import { FieldChipNode } from './FieldChipNode';
import { FieldTypeaheadPlugin } from './FieldTypeaheadPlugin';
import { FunctionNameNode, FunctionNamePlugin } from './FunctionNamePlugin';
import { FunctionTypeaheadPlugin } from './FunctionTypeaheadPlugin';
import { HintOnDemandPlugin } from './HintOnDemandPlugin';
import { HoverDocPlugin } from './HoverDocPlugin';
import { NumberLiteralNode, NumberLiteralPlugin } from './NumberLiteralPlugin';
import { SignatureHintPlugin } from './SignatureHintPlugin';
import { StringLiteralNode, StringLiteralPlugin } from './StringLiteralPlugin';
import { VALUE_TYPE_COLORS } from './typeColors';
import { $initFromCanonical, $serializeFormula, chipTargets, FormulaSerialized } from './serialization';

const containerCss = css`
  position: relative;
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 4px;
  background: #fff;

  &:focus-within {
    border-color: var(--mui-colors-secondary-50);
  }
`;

const containerErrorCss = css`
  border-color: var(--mui-colors-critical-50);

  &:focus-within {
    border-color: var(--mui-colors-critical-50);
  }
`;

const editorInputCss = css`
  min-height: 5rem;
  padding: 0.75rem;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;

  .formula-field-chip {
    ${chipShapeCss}
    ${CHIP_VARIANT_CSS.field}
  }

  .formula-field-chip--list {
    ${CHIP_VARIANT_CSS.list}
  }

  .formula-field-chip--member {
    ${CHIP_VARIANT_CSS.member}
  }

  .formula-string-literal {
    color: ${VALUE_TYPE_COLORS.string};
  }

  .formula-number-literal {
    color: ${VALUE_TYPE_COLORS.number};
  }

  .formula-function-name {
    color: var(--mui-colors-primary-50);
  }
`;

/**
 * Track each menu by name: the typeahead plugin re-fires onOpen on every
 * resolution update without a paired onClose, so counting events would drift —
 * set membership makes repeats idempotent. Returns whether any menu is open.
 */
export function updateOpenMenus(openMenus: Set<string>, menu: string, open: boolean): boolean {
  if (open) {
    openMenus.add(menu);
  } else {
    openMenus.delete(menu);
  }
  return openMenus.size > 0;
}

/**
 * Reports the content-editable's own focus. Lexical's commands rather than focus events on
 * the wrapper: the typeahead menus render in portals, so a container-level focusout would
 * fire while the user is picking an option. Both handlers return false — they observe only.
 */
function FocusReportPlugin({ onFocusChange }: { onFocusChange: (focused: boolean) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          FOCUS_COMMAND,
          () => {
            onFocusChange(true);
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          BLUR_COMMAND,
          () => {
            onFocusChange(false);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, onFocusChange]
  );

  return null;
}

const placeholderCss = css`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  color: var(--mui-colors-neutral-50);
  font-size: 0.875rem;
  user-select: none;
  pointer-events: none;
`;

export interface FormulaEditorProps {
  /** The vocabulary: the records the menus navigate plus the referenceable fields (chips). */
  fields: FieldRefTarget[];
  /** Stored canonical text to load (edit mode); the vocabulary must already be loaded. */
  initialFormula?: string;
  onChange: (serialized: FormulaSerialized) => void;
  label: string;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
  /** The factory renders its own Label; pass false to keep only the aria-label. */
  showLabel?: boolean;
  /** Display-text range to mark with a wavy error underline. */
  errorRange?: TextRange | null;
  /** Fires when a typeahead menu opens/closes — lets the caller hold back error surfacing. */
  onTypeaheadOpenChange?: (open: boolean) => void;
  /** Fires when the editor gains or loses focus — lets the caller swap its helper text. */
  onFocusChange?: (focused: boolean) => void;
  /** Escape hatch for focus control and tests. */
  editorRef?: RefObject<LexicalEditor | null>;
}

export function FormulaEditor({
  fields,
  initialFormula,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  showLabel = true,
  errorRange,
  onTypeaheadOpenChange,
  onFocusChange,
  editorRef,
}: FormulaEditorProps) {
  const targets = useMemo(() => chipTargets(fields), [fields]);

  const openMenusRef = useRef(new Set<string>());
  // Hover docs stay out of the way while any menu is open — a state (not just
  // the ref) so an already-visible hover popup closes when a menu opens.
  const [anyMenuOpen, setAnyMenuOpen] = useState(false);
  const reportMenuOpenChange = useCallback(
    (menu: string, open: boolean) => {
      const anyOpen = updateOpenMenus(openMenusRef.current, menu, open);
      setAnyMenuOpen(anyOpen);
      onTypeaheadOpenChange?.(anyOpen);
    },
    [onTypeaheadOpenChange]
  );
  const handleFieldMenuOpenChange = useCallback(
    (open: boolean) => reportMenuOpenChange('field', open),
    [reportMenuOpenChange]
  );
  const handleFunctionMenuOpenChange = useCallback(
    (open: boolean) => reportMenuOpenChange('function', open),
    [reportMenuOpenChange]
  );
  const handleHintMenuOpenChange = useCallback(
    (open: boolean) => reportMenuOpenChange('hint', open),
    [reportMenuOpenChange]
  );

  // Captured once: Lexical owns the content after mount, props only feed the plugins.
  const [initialConfig] = useState<InitialConfigType>(() => ({
    namespace: 'FormulaEditor',
    nodes: [FieldChipNode, StringLiteralNode, NumberLiteralNode, FunctionNameNode],
    // Rethrow like getLexicalEditorBaseConfig: an error here means corrupted
    // editor state, and the app's error boundaries/Sentry should see it.
    onError: (err: Error) => {
      throw err;
    },
    editorState: initialFormula ? () => $initFromCanonical(initialFormula, targets) : undefined,
  }));

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => onChange($serializeFormula()));
    },
    [onChange]
  );

  return (
    <div>
      {showLabel && (
        <FormLabel sx={{ display: 'block', mb: '0.25rem', fontSize: '0.75rem' }} error={error}>
          {label}
        </FormLabel>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <div css={[containerCss, error && containerErrorCss]}>
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                css={editorInputCss}
                aria-label={label}
                // Formula text is code: field refs ([Metrics.FMV], [Metrics.EBITDA]) are not
                // misspellings, and the browser's own autocorrect/smart quotes
                // would silently rewrite what the parser reads.
                spellCheck={false}
                autoCorrect='off'
                autoCapitalize='off'
                autoComplete='off'
              />
            }
            placeholder={placeholder ? <div css={placeholderCss}>{placeholder}</div> : null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ErrorUnderlinePlugin range={errorRange ?? null} />
        </div>
        <OnChangePlugin ignoreSelectionChange onChange={handleChange} />
        <HistoryPlugin />
        <FieldTypeaheadPlugin fields={fields} onOpenChange={handleFieldMenuOpenChange} />
        <FunctionTypeaheadPlugin onOpenChange={handleFunctionMenuOpenChange} />
        <HintOnDemandPlugin fields={fields} onOpenChange={handleHintMenuOpenChange} />
        <HoverDocPlugin fields={fields} suppressed={anyMenuOpen} />
        <SignatureHintPlugin suppressed={anyMenuOpen} />
        <AutoTokenizePlugin fields={fields} />
        <CommaCompletionPlugin fields={fields} />
        {onFocusChange && <FocusReportPlugin onFocusChange={onFocusChange} />}
        <StringLiteralPlugin />
        <NumberLiteralPlugin />
        <FunctionNamePlugin />
        {editorRef && <EditorRefPlugin editorRef={editorRef} />}
      </LexicalComposer>
      {helperText != null && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </div>
  );
}
